import queryString from 'query-string';

import {apiHost} from 'common/report/util';
import {apiClient} from 'common/util/api-client';

import {getCommonApiQueryParams} from '../smart-hub/utils/common';

// alert api consumers expect {error, status} result rather than an error to be thrown  ¯\_(ツ)_/¯
/* eslint-disable local-rules/deprecated-member-access */

const processResponse = (response) => {
  if (response.ok || response.status === 200) {
    return response.json().then((results) => results.results);
  } else {
    return response.json().then((results) => {
      console.error(response.status, results.error);
      return {
        error: results.error,
        status: response.status,
      };
    });
  }
};

// Existing Smart-Hub
export class SmartHubApi {
  constructor(urlMap) {
    this.urls = urlMap;
    this.pendingRequests = {};
  }

  fetchUnreadCount() {
    return this.fetch(`GET`, this.urls.unreadCount(this.queryParams()))
      .then((resp) => resp.json())
      .then((resp) => resp.results);
  }

  /*
   * Return a promise that resolves to a list of alerts.
   */
  fetchAlerts({maxExclusiveMs, limit} = {}) {
    if (this.pendingRequests[maxExclusiveMs]) {
      return;
    }

    this.pendingRequests[maxExclusiveMs] = this.fetch(
      `GET`,
      this.urls.alerts(this.queryParams({maxExclusiveMs, limit})),
    );

    const deletePending = () => delete this.pendingRequests[maxExclusiveMs];
    this.pendingRequests[maxExclusiveMs].then(deletePending, deletePending);

    return this.pendingRequests[maxExclusiveMs]
      .then((response) => response.json())
      .then((response) => response.results);
  }

  markAlertsRead({minExclusiveMs} = {}) {
    return this.fetch(`PATCH`, this.urls.alerts(this.queryParams({minExclusiveMs})), {is_read: true}); //eslint-disable-line camelcase
  }

  fetch(method, url, body) {
    return apiClient.fetch(url, {method, body: this.encodeBody(body)});
  }

  queryParams({maxExclusiveMs, limit, minExclusiveMs} = {}) {
    const params = Object.assign(getCommonApiQueryParams(), {limit});

    if (maxExclusiveMs) {
      params.max_exclusive_ms = maxExclusiveMs; // eslint-disable-line camelcase
    }

    if (minExclusiveMs) {
      params.max_inclusive_ms = minExclusiveMs; //eslint-disable-line camelcase
    }

    return params;
  }

  encodeBody(body) {
    return body && JSON.stringify(body);
  }

  // Smart-Hub Config Beta methods
  createAlertSubscription(bookmarkId) {
    return this.fetch(`POST`, this.urls.subscription, {
      bookmark_id: bookmarkId, // eslint-disable-line camelcase
      paused: false,
    }).then((response) => processResponse(response));
  }

  updateAlertSubscription(alertId, body) {
    return this.fetch(`PATCH`, `${this.urls.subscription}${alertId}`, body).then((response) =>
      processResponse(response),
    );
  }

  getAlertSubscriptions() {
    return this.fetch(`GET`, this.urls.subscription).then((response) => processResponse(response));
  }
}

const urls = ({projectId, bookmarkId, workspaceId}) => {
  // report_id is the bookmark id, the API expects the name report_id as the parameter, which is our old name.
  // making a new API so this historical API shouldn't be changed
  const withDefaults = (query) => (bookmarkId ? {...{report_id: bookmarkId.toString()}, ...query} : query); // eslint-disable-line camelcase
  const encoded = (query) => queryString.stringify(withDefaults(query));
  const projectOrWorkspace = workspaceId ? `workspaces/${workspaceId}` : `projects/${projectId}`;
  const baseUrl = `${apiHost()}/api/app/${projectOrWorkspace}/smart-hub`;

  return {
    alerts: (query) => `${baseUrl}/alerts?${encoded(query)}`,
    subscription: `${baseUrl}/subscription/`,
    unreadCount: (query) => `${baseUrl}/unread-count?${encoded(query)}`,
  };
};

export const smartHubApi = ({projectId, bookmarkId = null, workspaceId = null}) =>
  new SmartHubApi(urls({projectId, bookmarkId, workspaceId}));
